import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ALL_STATES, STATE_HEX, DATE_REQUIRED_STATES,
  VIEW_ALL_ROLES, CAN_CREATE_ROLES, BACKEND_ASSIGNABLE_ROLES, INTERACTION_ASSIGNABLE_ROLES,
  LEAD_TYPES, REQUIREMENTS, LEAD_SOURCES, STORE_SUPER, isSuperStore, canViewAI,
} from "../lib/states";
import LeadCard from "../components/LeadCard";
import LeadDrawer from "../components/LeadDrawer";
import AddLeadModal from "../components/AddLeadModal";
import { RefreshCw, Search, Plus, Download, Sparkles, SlidersHorizontal, X, AlertTriangle, ChevronDown, CalendarDays } from "lucide-react";

const VISIBLE_PER_COLUMN = 60;

// Attributes available in the Pipeline view filter. `static` = fixed option list; otherwise the
// value list is derived from the loaded leads (distinct values).
const FILTER_ATTRS = [
  { key: "requirement", label: "Requirement", get: (l) => l.requirement, options: REQUIREMENTS },
  { key: "lead_type", label: "Lead Type", get: (l) => l.lead_type, options: LEAD_TYPES },
  { key: "source", label: "Source", get: (l) => l.source, options: LEAD_SOURCES },
  { key: "segment", label: "AI Segment", get: (l) => l.segment, aiOnly: true },
  { key: "state", label: "Stage", get: (l) => l.state, options: ALL_STATES },
  { key: "assignee_name", label: "Backend Assignee", get: (l) => l.assignee_name },
  { key: "interaction_assignee_name", label: "Interaction Assignee", get: (l) => l.interaction_assignee_name },
  // Boolean flags — exposed as Yes/No so they fit the same generic UI as the rest.
  { key: "is_hot", label: "Hot Lead", get: (l) => (l.is_hot ? "Yes" : "No"), options: ["Yes", "No"] },
  { key: "ai_suggested_hot", label: "AI Star", get: (l) => (l.ai_suggested_hot ? "Yes" : "No"), options: ["Yes", "No"], aiOnly: true },
];

export default function Board() {
  const { user } = useAuth();
  const role = user?.role;
  const showAssignee = VIEW_ALL_ROLES.includes(role);
  const canCreate = CAN_CREATE_ROLES.includes(role);
  const isAdmin = role === "admin" || role === "super_admin";

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [drawerState, setDrawerState] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [query, setQuery] = useState("");
  const [pulling, setPulling] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [callers, setCallers] = useState([]);
  const [interactionAssignees, setInteractionAssignees] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [boardView, setBoardView] = useState("native"); // "native" | "wati"
  const [filterAttr, setFilterAttr] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Store filter — Super Store users can scope the board to one store, or
  // see only currently-unassigned leads. Hidden entirely from non-Super users.
  const [storeFilter, setStoreFilter] = useState("");  // "" = all, "__unassigned__" = null, "<name>" = exact
  const [stores, setStores] = useState([]);
  const meIsSuper = isSuperStore(user);
  useEffect(() => {
    if (!meIsSuper) return;
    api.get("/stores").then((r) => setStores(r.data || [])).catch(() => {});
  }, [meIsSuper]);
  // Mobile only: stats panel is collapsed by default to keep the lead list
  // above the fold. The toggle button is rendered just for mobile (< sm).
  const [statsOpen, setStatsOpen] = useState(false);
  const [visibleCounts, setVisibleCounts] = useState({});
  // Per-column localized search — totally independent of the top-level `query`.
  // Keyed by columnId ("native:Fresh" / "wati:1- New Lead"); value is { open, q }.
  const [colSearch, setColSearch] = useState({});
  const setColumnSearch = (colId, partial) =>
    setColSearch((prev) => ({ ...prev, [colId]: { ...(prev[colId] || { open: false, q: "" }), ...partial } }));
  const filterColumnItems = (colId, items) => {
    const cs = colSearch[colId];
    if (!cs?.q) return items;
    const q = cs.q.toLowerCase();
    return items.filter((l) => l.name?.toLowerCase().includes(q) || l.phone?.includes(q));
  };

  const visibleFor = (key) => visibleCounts[key] || VISIBLE_PER_COLUMN;
  const loadMore = (key) =>
    setVisibleCounts((p) => ({ ...p, [key]: (p[key] || VISIBLE_PER_COLUMN) + VISIBLE_PER_COLUMN }));
  const onColScroll = (e, key, total) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120 && visibleFor(key) < total) loadMore(key);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
      api.get("/funnel").then((r) => setFunnel(r.data)).catch(() => {});
      api.get("/users")
        .then((r) => {
          const users = r.data;
          setCallers(users.filter((u) => BACKEND_ASSIGNABLE_ROLES.includes(u.role) && u.active !== false));
          setInteractionAssignees(users.filter((u) => INTERACTION_ASSIGNABLE_ROLES.includes(u.role) && u.active !== false));
        })
        .catch(() => {});
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Restart per-column paging whenever the visible dataset changes (search / filter / view).
  useEffect(() => { setVisibleCounts({}); }, [query, filterAttr, filterValue, dateFrom, dateTo, storeFilter, boardView]);

  const attrDef = FILTER_ATTRS.find((a) => a.key === filterAttr);
  const filterValueOptions = (() => {
    if (!attrDef) return [];
    let opts;
    if (attrDef.options) {
      opts = attrDef.options.map((v) => ({ value: v, label: v }));
    } else {
      const set = new Set();
      leads.forEach((l) => { const v = attrDef.get(l); if (v) set.add(v); });
      opts = Array.from(set).sort().map((v) => ({ value: v, label: v }));
    }
    if (leads.some((l) => !attrDef.get(l))) opts.push({ value: "__none__", label: "— None set —" });
    return opts;
  })();

  const clearFilter = () => { setFilterAttr(""); setFilterValue(""); setDateFrom(""); setDateTo(""); setStoreFilter(""); };

  let filtered = query
    ? leads.filter((l) => l.name?.toLowerCase().includes(query.toLowerCase()) || l.phone?.includes(query))
    : leads;
  if (attrDef && filterValue) {
    filtered = filtered.filter((l) => {
      const v = attrDef.get(l);
      return filterValue === "__none__" ? !v : v === filterValue;
    });
  }
  // Date range filter on lead arrival (`created_at`). Inclusive on both ends —
  // start counts the whole day from 00:00, end counts up to 23:59:59.999.
  if (dateFrom || dateTo) {
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : Infinity;
    filtered = filtered.filter((l) => {
      if (!l.created_at) return false;
      const t = new Date(l.created_at).getTime();
      return t >= fromMs && t <= toMs;
    });
  }
  // Store filter (Super-Store-only — others never even see the dropdown).
  if (meIsSuper && storeFilter) {
    if (storeFilter === "__unassigned__") {
      filtered = filtered.filter((l) => !l.store);
    } else {
      filtered = filtered.filter((l) => l.store === storeFilter);
    }
  }
  const filterActive = Boolean((attrDef && filterValue) || dateFrom || dateTo || storeFilter);
  // Two-tier work-queue sort applied to every column (see PRD 2026-07):
  //  Tier 1 (top): untouched leads (last_activity_at == null) — sort by
  //  stage_entered_at DESC so the newest arrivals sit at the very top.
  //  Tier 2 (below): touched leads — sort by last_activity_at ASC so the
  //  oldest-contacted lead rises to demand attention; the most recently
  //  called lead sinks to the bottom.
  //  Tie-breaker inside either tier: created_at DESC.
  const _ts = (v) => (v ? new Date(v).getTime() : 0);
  const sortWorkQueue = (a, b) => {
    const aTouched = !!a.last_activity_at;
    const bTouched = !!b.last_activity_at;
    if (aTouched !== bTouched) return aTouched ? 1 : -1;
    if (!aTouched) {
      const d = _ts(b.stage_entered_at) - _ts(a.stage_entered_at);
      if (d) return d;
    } else {
      const d = _ts(a.last_activity_at) - _ts(b.last_activity_at);
      if (d) return d;
    }
    return _ts(b.created_at) - _ts(a.created_at);
  };
  const grouped = ALL_STATES.reduce((acc, s) => {
    acc[s] = filtered.filter((l) => l.state === s).sort(sortWorkQueue);
    return acc;
  }, {});

  // New-leads counter (Today / Yesterday / Day-before). Uses IST date-keys
  // because the business operates out of Bangalore and all lead `created_at`
  // values come from WATI / TeleCRM in IST. Counts the FULL lead set the user
  // can see (independent of board filters) so the counter stays a stable
  // "incoming volume" pulse.
  const istKey = (d) => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(d);
  const today = new Date();
  const yest = new Date(today); yest.setDate(yest.getDate() - 1);
  const dayB = new Date(today); dayB.setDate(dayB.getDate() - 2);
  const todayKey = istKey(today), yestKey = istKey(yest), dayBKey = istKey(dayB);
  const newLeadCounts = leads.reduce((acc, l) => {
    if (!l.created_at) return acc;
    const k = istKey(new Date(l.created_at));
    if (k === todayKey) acc.today += 1;
    else if (k === yestKey) acc.yesterday += 1;
    else if (k === dayBKey) acc.dayBefore += 1;
    return acc;
  }, { today: 0, yesterday: 0, dayBefore: 0 });

  const enrichedCount = leads.filter((l) => l.enrich_status === "ok").length;
  const failedCount = leads.filter((l) => l.enrich_status === "failed").length;
  const enrichTotal = leads.length;
  const enrichPct = enrichTotal ? Math.round((enrichedCount / enrichTotal) * 100) : 0;

  const NO_WATI = "— No WATI stage —";
  const watiColumns = (() => {
    const map = {};
    filtered.forEach((l) => {
      const k = l.wati_lead_stage || NO_WATI;
      (map[k] = map[k] || []).push(l);
    });
    return Object.keys(map)
      .sort((a, b) => (a === NO_WATI ? 1 : b === NO_WATI ? -1 : a.localeCompare(b)))
      .map((k) => ({ key: k, items: map[k] }));
  })();

  const onDragStart = useCallback((e, lead) => {
    if (!lead.can_edit) { e.preventDefault(); return; }
    e.dataTransfer.setData("leadId", lead.id);
    e.dataTransfer.setData("from", lead.state);
  }, []);

  const onCardClick = useCallback((l) => { setActive(l); setDrawerState(null); }, []);

  const onDrop = async (e, toState) => {
    e.preventDefault();
    setDragOver(null);
    const leadId = e.dataTransfer.getData("leadId");
    const from = e.dataTransfer.getData("from");
    if (!leadId || from === toState) return;
    const lead = leads.find((l) => l.id === leadId);
    if (DATE_REQUIRED_STATES.includes(toState)) {
      setActive(lead); setDrawerState(toState); return;
    }
    setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, state: toState } : l)));
    try {
      await api.post(`/leads/${leadId}/action`, { new_state: toState });
      toast.success(`Moved to ${toState}`);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      load();
    }
  };

  const pullNow = async () => {
    setPulling(true);
    try {
      const { data } = await api.post("/intake/pull");
      if (data.error) toast.warning(data.error);
      else toast.success(`Pulled ${data.ingested} new lead(s), skipped ${data.skipped}.`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setPulling(false);
    }
  };

  const exportBackup = async () => {
    try {
      const res = await api.get("/export/leads.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `unwind-crm-backup-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 sm:px-6 py-2.5 sm:py-0 sm:h-16 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5 border-b border-stone-200 bg-[#FAFAF9]">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">Pipeline</h1>
          <div className="flex items-center gap-2.5 flex-wrap">
            <p className="text-xs text-stone-500">{filtered.length} leads {showAssignee ? "· all" : "· assigned to you"}</p>
            {enrichTotal > 0 && (
              <div className="flex items-center gap-1.5" data-testid="enrichment-indicator" title={`AI enrichment: ${enrichedCount} of ${enrichTotal} leads researched (${enrichPct}%)`}>
                <Sparkles className="h-3 w-3 text-violet-500" />
                <span className="text-xs font-medium text-violet-700" data-testid="enrichment-count">{enrichedCount}/{enrichTotal}</span>
                <div className="h-1.5 w-16 rounded-full bg-stone-200 overflow-hidden">
                  <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${enrichPct}%` }} />
                </div>
              </div>
            )}
            {failedCount > 0 && (
              <span data-testid="enrichment-failed" title={`${failedCount} AI lookups failed — open Research to retry`}
                className="flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                <AlertTriangle className="h-3 w-3" /> {failedCount} failed
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3">
          {meIsSuper && stores.filter((s) => s.name !== STORE_SUPER).length > 0 && (
            <select
              value={storeFilter}
              onChange={(e) => setStoreFilter(e.target.value)}
              data-testid="filter-store"
              aria-label="Filter by store"
              title="Filter by store"
              className="text-sm border border-stone-300 rounded-md bg-white px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-stone-900/60 max-w-[160px]">
              <option value="">All stores</option>
              <option value="__unassigned__">Unassigned</option>
              {stores.filter((s) => s.name !== STORE_SUPER).map((s) => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          )}
          <div className="relative flex-1 sm:flex-none min-w-[140px]">
            <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or phone…" data-testid="board-search"
              className="pl-9 pr-3 py-2 text-sm border border-stone-300 rounded-md bg-white w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-stone-900/60" />
          </div>
          <div className="flex items-center rounded-md border border-stone-300 overflow-hidden text-sm shrink-0" data-testid="board-view-toggle">
            <button onClick={() => setBoardView("native")} data-testid="view-native"
              className={`px-3 py-2 transition-colors ${boardView === "native" ? "bg-stone-900 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}>Stages</button>
            <button onClick={() => setBoardView("wati")} data-testid="view-wati"
              className={`px-3 py-2 transition-colors border-l border-stone-300 ${boardView === "wati" ? "bg-stone-900 text-white" : "bg-white text-stone-600 hover:bg-stone-50"}`}>WATI</button>
          </div>
          {canCreate && (
            <button onClick={() => setShowAdd(true)} data-testid="add-lead-button"
              className="flex items-center gap-2 border border-stone-300 bg-white hover:border-stone-400 text-stone-800 text-sm px-3 sm:px-4 py-2 rounded-md transition-colors">
              <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Add lead</span>
            </button>
          )}
          {isAdmin && (
            <button onClick={exportBackup} data-testid="export-backup-button" title="Download full CRM backup (CSV)"
              className="flex items-center gap-2 border border-stone-300 bg-white hover:border-stone-400 text-stone-800 text-sm px-3 sm:px-4 py-2 rounded-md transition-colors">
              <Download className="h-4 w-4" /> <span className="hidden sm:inline">Backup</span>
            </button>
          )}
          {isAdmin && (
            <button onClick={pullNow} disabled={pulling} data-testid="pull-now-button"
              className="flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm px-3 sm:px-4 py-2 rounded-md transition-colors disabled:opacity-60">
              <RefreshCw className={`h-4 w-4 ${pulling ? "animate-spin" : ""}`} /> <span className="hidden sm:inline">Pull now</span>
            </button>
          )}
        </div>
      </div>

      {!loading && (
        <div className="shrink-0 px-4 sm:px-6 py-2 bg-[#FAFAF9] flex items-center gap-2 flex-wrap" data-testid="board-filter-bar">
          <SlidersHorizontal className="h-4 w-4 text-stone-400 shrink-0" />
          <span className="text-xs text-stone-500 shrink-0">Filter</span>
          <select value={filterAttr} onChange={(e) => { setFilterAttr(e.target.value); setFilterValue(""); }}
            data-testid="filter-attribute"
            className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60">
            <option value="">Attribute…</option>
            {FILTER_ATTRS.filter((a) => !a.aiOnly || canViewAI(user)).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
          </select>
          {attrDef && (
            <select value={filterValue} onChange={(e) => setFilterValue(e.target.value)}
              data-testid="filter-value"
              className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60 max-w-[200px]">
              <option value="">Value…</option>
              {filterValueOptions.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          )}
          <span className="text-xs text-stone-400 shrink-0 pl-1.5 border-l border-stone-200 ml-1">Arrived</span>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="filter-date-from"
            aria-label="From date"
            className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60"
          />
          <span className="text-xs text-stone-400">to</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="filter-date-to"
            aria-label="To date"
            className="border border-stone-300 rounded-md px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/60"
          />
          {filterActive && (
            <>
              <span className="text-xs font-medium text-stone-800 tabular-nums" data-testid="filter-result-count">
                {filtered.length} match{filtered.length === 1 ? "" : "es"}
              </span>
              <button onClick={clearFilter} data-testid="filter-clear"
                className="flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800 transition-colors">
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            </>
          )}
        </div>
      )}

      {/* "Show stats" toggle — same behaviour on mobile + desktop now, collapsed by default. */}
      {!loading && (funnel || true) && (
        <div className="shrink-0 px-4 sm:px-6 py-1.5 bg-[#FAFAF9] border-b border-stone-200/70">
          <button
            type="button"
            onClick={() => setStatsOpen((v) => !v)}
            aria-expanded={statsOpen}
            aria-controls="board-stats-collapsible"
            data-testid="stats-toggle"
            className="w-full flex items-center justify-between text-xs font-medium text-stone-600 hover:text-stone-900 transition-colors"
          >
            <span className="flex items-center gap-1.5">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {statsOpen ? "Hide stats" : "Show stats"}
              <span className="text-stone-400 font-normal">· funnel & stage counts</span>
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${statsOpen ? "rotate-180" : ""}`} />
          </button>
        </div>
      )}

      {!loading && funnel && (
        <div
          id="board-stats-collapsible"
          className={`shrink-0 px-4 sm:px-6 pt-3 bg-[#FAFAF9] ${statsOpen ? "" : "hidden"}`}
        >
          {/* New-leads pulse — quick read on incoming volume over the last 3 days. */}
          <div className="flex flex-wrap items-center gap-2.5 mb-2.5" data-testid="new-leads-counter">
            <div className="flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-semibold text-stone-500 shrink-0">
              <CalendarDays className="h-3.5 w-3.5" /> New leads
            </div>
            {[
              { key: "today", label: "Today", n: newLeadCounts.today },
              { key: "yesterday", label: "Yesterday", n: newLeadCounts.yesterday },
              { key: "day_before", label: "Day before", n: newLeadCounts.dayBefore },
            ].map((c) => (
              <div key={c.key} data-testid={`new-leads-${c.key}`}
                className="flex items-center gap-3 bg-white border border-stone-200 rounded-lg px-3.5 py-1.5 shadow-sm">
                <span className="text-xs font-medium text-stone-500">{c.label}</span>
                <span className="text-xl font-heading font-semibold text-stone-900 tabular-nums">{c.n}</span>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="funnel-stats">
            <FunnelStat testid="funnel-fresh-booked" label="Fresh → Interaction Booked"
              pct={funnel.fresh_to_booked} n={funnel.interaction_booked} d={funnel.total} color="#6366f1" />
            <FunnelStat testid="funnel-booked-completed" label="Booked → Completed"
              pct={funnel.booked_to_completed} n={funnel.interaction_completed} d={funnel.interaction_booked} color="#0d9488" />
            <FunnelStat testid="funnel-completed-won" label="Completed → Deal Won"
              pct={funnel.completed_to_won} n={funnel.deal_won} d={funnel.interaction_completed} color="#16a34a" />
          </div>
        </div>
      )}

      {!loading && (
        <div className={`shrink-0 px-4 sm:px-6 py-2 bg-[#FAFAF9] border-b border-stone-200/70 ${statsOpen ? "" : "hidden"}`}>
          <div className="flex flex-wrap gap-1.5" data-testid="stage-counts">
            {boardView === "native"
              ? ALL_STATES.map((s) => {
                  const c = (grouped[s] || []).length;
                  return (
                    <div key={s} data-testid={`stage-count-${s}`}
                      className="flex items-center gap-1.5 rounded-md border bg-white px-2 py-0.5"
                      style={{ borderColor: (STATE_HEX[s] || "#64748b") + "33" }}>
                      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: STATE_HEX[s] || "#64748b" }} />
                      <span className="text-[11px] text-stone-500 leading-none whitespace-nowrap">{s}</span>
                      <span className="text-[11px] font-bold leading-none tabular-nums" style={{ color: STATE_HEX[s] || "#475569" }}>{c}</span>
                    </div>
                  );
                })
              : (
                <div className="flex items-center gap-1.5 text-[11px] text-stone-500" data-testid="wati-view-note">
                  Grouped by WATI lead stage · {watiColumns.length} stage(s) · read-only (cards reflect the WATI sheet)
                </div>
              )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex-1 flex items-center justify-center text-stone-400">Loading pipeline…</div>
      ) : boardView === "wati" ? (
        <div className="flex-1 overflow-x-auto overflow-y-hidden kanban-scroll px-4 sm:px-6 py-5" data-testid="wati-board">
          <div className="flex gap-4 h-full items-start min-w-max">
            {watiColumns.length === 0 && (
              <div className="text-sm text-stone-400">No WATI stage data yet. Run "Pull now" once WATI populates lead stages.</div>
            )}
            {watiColumns.map(({ key, items: rawItems }) => {
              const colId = `wati:${key}`;
              const cs = colSearch[colId] || { open: false, q: "" };
              const items = filterColumnItems(colId, rawItems);
              return (
              <div key={key} data-testid={`wati-column-${key}`}
                className="w-[280px] shrink-0 flex flex-col rounded-xl border border-stone-200/70 bg-white/40 h-full">
                <div className="px-3.5 py-3 rounded-t-xl border-b border-amber-200 bg-amber-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-amber-800 truncate">{key}</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setColumnSearch(colId, { open: !cs.open, q: cs.open ? "" : cs.q })}
                        data-testid={`col-search-toggle-${colId}`}
                        aria-label={cs.open ? "Close column search" : "Search in this column"}
                        title="Search in this column"
                        className={`h-6 w-6 flex items-center justify-center rounded-md transition-colors ${cs.open || cs.q ? "bg-amber-200 text-amber-900" : "text-amber-700 hover:bg-amber-200/70"}`}>
                        <Search className="h-3.5 w-3.5" />
                      </button>
                      <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-white border border-amber-200 text-amber-700">{items.length}{cs.q ? `/${rawItems.length}` : ""}</span>
                    </div>
                  </div>
                  {cs.open && (
                    <input
                      autoFocus
                      type="text"
                      value={cs.q}
                      onChange={(e) => setColumnSearch(colId, { q: e.target.value })}
                      onKeyDown={(e) => e.key === "Escape" && setColumnSearch(colId, { open: false, q: "" })}
                      placeholder="Search name or phone…"
                      data-testid={`col-search-input-${colId}`}
                      className="mt-2 w-full text-xs bg-white border border-amber-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-400/60 placeholder:text-amber-400"
                    />
                  )}
                </div>
                <div onScroll={(e) => onColScroll(e, `wati:${key}`, items.length)}
                  className="flex-1 overflow-y-auto col-scroll px-2.5 py-3 space-y-2.5">
                  {items.slice(0, visibleFor(`wati:${key}`)).map((lead) => (
                    <LeadCard key={lead.id} lead={lead} showAssignee={showAssignee}
                      onClick={onCardClick} onDragStart={onDragStart} />
                  ))}
                  {items.length > visibleFor(`wati:${key}`) && (
                    <button onClick={() => loadMore(`wati:${key}`)} data-testid={`load-more-wati-${key}`}
                      className="w-full text-xs font-medium text-stone-800 hover:text-stone-800 text-center py-2.5 bg-stone-100 hover:bg-stone-200 border border-stone-300 rounded-lg transition-colors">
                      Load {Math.min(VISIBLE_PER_COLUMN, items.length - visibleFor(`wati:${key}`))} more · {items.length - visibleFor(`wati:${key}`)} remaining
                    </button>
                  )}
                  {items.length === 0 && cs.q && (
                    <div className="text-xs text-stone-300 text-center py-6 border border-dashed border-stone-200 rounded-lg">No matches for &ldquo;{cs.q}&rdquo;</div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-x-auto overflow-y-hidden kanban-scroll px-4 sm:px-6 py-5">
          <div className="flex gap-4 h-full items-start min-w-max">
            {ALL_STATES.map((state) => {
              const rawItems = grouped[state] || [];
              const colId = `native:${state}`;
              const cs = colSearch[colId] || { open: false, q: "" };
              const items = filterColumnItems(colId, rawItems);
              return (
                <div key={state}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(state); }}
                  onDragLeave={() => setDragOver((s) => (s === state ? null : s))}
                  onDrop={(e) => onDrop(e, state)}
                  data-testid={`column-${state}`}
                  className={`w-[280px] shrink-0 flex flex-col rounded-xl border transition-colors h-full ${dragOver === state ? "border-stone-400 bg-stone-100/60" : "border-stone-200/70 bg-white/40"}`}>
                  <div className="px-3.5 py-3 rounded-t-xl border-b"
                    style={{ backgroundColor: (STATE_HEX[state] || "#64748b") + "14", borderColor: (STATE_HEX[state] || "#64748b") + "33" }}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: STATE_HEX[state] || "#64748b" }} />
                        <span className="text-sm font-semibold" style={{ color: STATE_HEX[state] || "#475569" }}>{state}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setColumnSearch(colId, { open: !cs.open, q: cs.open ? "" : cs.q })}
                          data-testid={`col-search-toggle-${colId}`}
                          aria-label={cs.open ? "Close column search" : "Search in this column"}
                          title="Search in this column"
                          className={`h-6 w-6 flex items-center justify-center rounded-md transition-colors ${cs.open || cs.q ? "bg-stone-900 text-white" : "text-stone-500 hover:bg-stone-200"}`}
                          style={cs.open || cs.q ? {} : { color: STATE_HEX[state] || "#475569" }}>
                          <Search className="h-3.5 w-3.5" />
                        </button>
                        <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-white border"
                          style={{ color: STATE_HEX[state] || "#64748b", borderColor: (STATE_HEX[state] || "#64748b") + "40" }}>{items.length}{cs.q ? `/${rawItems.length}` : ""}</span>
                      </div>
                    </div>
                    {cs.open && (
                      <input
                        autoFocus
                        type="text"
                        value={cs.q}
                        onChange={(e) => setColumnSearch(colId, { q: e.target.value })}
                        onKeyDown={(e) => e.key === "Escape" && setColumnSearch(colId, { open: false, q: "" })}
                        placeholder="Search name or phone…"
                        data-testid={`col-search-input-${colId}`}
                        className="mt-2 w-full text-xs bg-white border border-stone-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-stone-900/40 placeholder:text-stone-400"
                      />
                    )}
                  </div>
                  <div onScroll={(e) => onColScroll(e, `native:${state}`, items.length)}
                    className="flex-1 overflow-y-auto col-scroll px-2.5 py-3 space-y-2.5">
                    {items.slice(0, visibleFor(`native:${state}`)).map((lead) => (
                      <LeadCard key={lead.id} lead={lead} showAssignee={showAssignee}
                        onClick={onCardClick} onDragStart={onDragStart} />
                    ))}
                    {items.length > visibleFor(`native:${state}`) && (
                      <button onClick={() => loadMore(`native:${state}`)} data-testid={`more-${state}`}
                        className="w-full text-xs font-medium text-stone-800 hover:text-stone-800 text-center py-2.5 bg-stone-100 hover:bg-stone-200 border border-stone-300 rounded-lg transition-colors">
                        Load {Math.min(VISIBLE_PER_COLUMN, items.length - visibleFor(`native:${state}`))} more · {items.length - visibleFor(`native:${state}`)} remaining
                      </button>
                    )}
                    {items.length === 0 && !cs.q && (
                      <div className="text-xs text-stone-300 text-center py-6 border border-dashed border-stone-200 rounded-lg">Drop here</div>
                    )}
                    {items.length === 0 && cs.q && (
                      <div className="text-xs text-stone-400 text-center py-6 border border-dashed border-stone-200 rounded-lg">No matches for &ldquo;{cs.q}&rdquo;</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {active && (
        <LeadDrawer leadId={active.id} presetState={drawerState}
          onClose={() => { setActive(null); setDrawerState(null); }} onSaved={() => load()} />
      )}
      {showAdd && <AddLeadModal callers={callers} interactionAssignees={interactionAssignees} onClose={() => setShowAdd(false)} onCreated={() => load()} />}
    </div>
  );
}

function FunnelStat({ label, pct, n, d, color, testid }) {
  return (
    <div data-testid={testid}
      className="flex items-center gap-2.5 rounded-lg border border-stone-200 bg-white px-3 py-1.5 shadow-sm">
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-[11px] font-medium text-stone-500 whitespace-nowrap">{label}</span>
      <span className="text-base font-bold tabular-nums leading-none" style={{ color }}>{pct}%</span>
      <span className="text-[11px] text-stone-400 tabular-nums whitespace-nowrap">{n}/{d}</span>
    </div>
  );
}
